import { useEffect, useState } from 'react';
import { CloudOff, RefreshCw, Check } from 'lucide-react';
import { lastSyncedAt, onQueueChange, queueCount } from '../lib/offlineQueue';
import { drainQueue, startSyncWatcher } from '../services/sync';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { relativeTime } from '../lib/format';
import { Badge } from './ui';

export default function SyncIndicator() {
  const online = useOnlineStatus();
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState<Date | null>(() => lastSyncedAt());

  useEffect(() => {
    const refresh = () => {
      void queueCount().then(setPending);
      setLastSync(lastSyncedAt());
    };
    refresh();
    const unsubQueue = onQueueChange(refresh);
    const stopWatcher = startSyncWatcher();
    const interval = window.setInterval(refresh, 20_000);
    return () => {
      unsubQueue();
      stopWatcher();
      window.clearInterval(interval);
    };
  }, []);

  async function syncNow() {
    setSyncing(true);
    try {
      await drainQueue();
    } finally {
      setSyncing(false);
      setPending(await queueCount());
      setLastSync(lastSyncedAt());
    }
  }

  if (!online) {
    return (
      <Badge tone="warn" className="whitespace-nowrap">
        <CloudOff size={12} />
        Offline{pending > 0 ? ` · ${pending} queued` : ''}
      </Badge>
    );
  }

  if (pending > 0) {
    return (
      <button onClick={syncNow} disabled={syncing} title={`Last sync ${relativeTime(lastSync)}`}>
        <Badge tone="info" className="whitespace-nowrap">
          <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} />
          {syncing ? 'Syncing…' : `${pending} to upload`}
        </Badge>
      </button>
    );
  }

  return (
    <span className="hidden sm:inline" title={`Last sync ${relativeTime(lastSync)}`}>
      <Badge tone="good" className="whitespace-nowrap">
        <Check size={12} />
        Synced
      </Badge>
    </span>
  );
}
